import { useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ReactComponent as DigntecLogo } from '../../Assets/images/DigntecLogo.svg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { api } from '../../api/api';
import toast from 'react-hot-toast';
import './login.css';

function EmailRegister() {
  const { auth } = useSelector(s => s.User);
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [phone, setPhone] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [town, setTown] = useState('');
  const [state, setState] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storeType, setStoreType] = useState('new'); // 가입 방법 A: 신규/기존 (12.01-D011)
  const [screenCount, setScreenCount] = useState(3); // TV(스크린) 수 — 나중에 바꿀 수 있음
  const [role, setRole] = useState('store_owner');
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);

  // 초대 토큰 검증
  useEffect(() => {
    if (inviteToken) {
      api.get(`/user/verify-invite/${inviteToken}`)
        .then(res => {
          setInviteData(res.data);
          setEmail(res.data.email || '');
          setRole(res.data.role || 'store_owner');
          setUsername(res.data.username || '');
        })
        .catch(() => toast.error('Invalid or expired invitation'));
    }
  }, [inviteToken]);

  // Zip → City/State
  useEffect(() => {
    const zip = zipcode.replace(/\D/g, '');
    if (zip.length === 5) {
      api.get(`/zipcode/${zip}`).then(res => {
        setTown(res.data.city || '');
        setState(res.data.stateAbbr || '');
        if (!storeName) setStoreName(`${res.data.stateAbbr || ''}_${(res.data.city || '').replace(/\s+/g, '')}`);
        setTimeout(() => document.getElementById('reg-phone')?.focus(), 100);
      }).catch(() => {});
    }
  }, [zipcode]);

  const fmtPhone = (val) => {
    const d = val.replace(/\D/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const handleSubmit = async () => {
    if (!email || !username || !password) return toast.error('Fill required fields');
    if (password.length < 6) return toast.error('Password must be 6+ characters');
    if (password !== confirmPw) return toast.error('Passwords do not match');

    setSubmitting(true);
    try {
      await api.post('/user/email-register', {
        email, username, password, phone: phone.replace(/\D/g, ''),
        zipcode, town, state, storeName, role, storeType, screenCount: storeType === 'new' ? screenCount : undefined,
        inviteToken: inviteToken || undefined,
      });
      setStep(2);
      toast.success('Registration complete!');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Registration failed');
    }
    setSubmitting(false);
  };

  if (auth) return <Navigate to="/" />;

  return (
    <div className="lp-root">
      <div className="lp-left">
        <div className="lp-brand">
          <div className="lp-brand-logo">
            <DigntecLogo style={{ width: '160px', height: 'auto', filter: 'brightness(0) invert(1)' }} />
          </div>
          <h1 className="lp-brand-title">Email<br />Registration</h1>
          <p className="lp-brand-sub">
            {inviteData
              ? `You've been invited as ${inviteData.role?.replace('_', ' ')}. Complete your registration.`
              : 'Register with your email address to get started.'}
          </p>
        </div>
      </div>

      <div className="lp-right">
        <div className="lp-form-wrap">
          {step === 1 ? (
            <>
              <div className="lp-form-header">
                <h2 className="lp-form-title">
                  {inviteData ? 'Complete Registration' : 'Create Account'}
                </h2>
                <p className="lp-form-subtitle">
                  {inviteData ? `Invited as: ${inviteData.role?.replace('_', ' ')}` : 'Fill in your details'}
                </p>
              </div>

              <div className="lp-field">
                <label className="lp-label">Email *</label>
                <input className="lp-input" type="email" value={email}
                  onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
                  readOnly={!!inviteData?.email} style={inviteData?.email ? { background: '#f9fafb' } : {}} />
              </div>

              <div className="lp-field">
                <label className="lp-label">Name *</label>
                <input className="lp-input" value={username}
                  onChange={e => setUsername(e.target.value)} placeholder="Full Name" />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <div className="lp-field" style={{ flex: 1 }}>
                  <label className="lp-label">Password *</label>
                  <input className="lp-input" type="password" value={password}
                    onChange={e => setPassword(e.target.value)} placeholder="6+ characters" />
                </div>
                <div className="lp-field" style={{ flex: 1 }}>
                  <label className="lp-label">Confirm *</label>
                  <input className="lp-input" type="password" value={confirmPw}
                    onChange={e => setConfirmPw(e.target.value)} placeholder="Confirm" />
                </div>
              </div>

              {/* 점주용 추가 필드 (디자이너/매니저는 숨김) */}
              {(role === 'store_owner' || role === 'store_manager' || !inviteData) && (
                <>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div className="lp-field" style={{ flex: 1 }}>
                      <label className="lp-label">Zip Code</label>
                      <input className="lp-input" value={zipcode}
                        onChange={e => setZipcode(e.target.value)} placeholder="90210" maxLength={5} />
                    </div>
                    <div className="lp-field" style={{ flex: 1 }}>
                      <label className="lp-label">City</label>
                      <input className="lp-input" value={town} readOnly style={{ background: '#f9fafb' }} />
                    </div>
                    <div className="lp-field" style={{ flex: 0.5 }}>
                      <label className="lp-label">State</label>
                      <input className="lp-input" value={state} readOnly style={{ background: '#f9fafb' }} />
                    </div>
                  </div>

                  <div className="lp-field">
                    <label className="lp-label">Phone</label>
                    <input id="reg-phone" className="lp-input" type="tel" value={phone}
                      onChange={e => setPhone(fmtPhone(e.target.value))} placeholder="(000) 000-0000" />
                  </div>

                  <div className="lp-field">
                    <label className="lp-label">Store Name</label>
                    <input className="lp-input" value={storeName}
                      onChange={e => setStoreName(e.target.value)} placeholder="Auto-generated" />
                  </div>

                  <div className="lp-field">
                    <label className="lp-label">매장 구분 · Store type</label>
                    <select className="lp-input" value={storeType} onChange={e => setStoreType(e.target.value)}>
                      <option value="new">신규 매장 (새로 설치) · New store</option>
                      <option value="existing">이미 Digntec 을 쓰는 매장 · Existing store</option>
                    </select>
                  </div>
                  {storeType === 'new' && (
                    <div className="lp-field">
                      <label className="lp-label">TV(스크린) 수 · Number of screens</label>
                      <select className="lp-input" value={screenCount} onChange={e => setScreenCount(Number(e.target.value))}>
                        {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>나중에 바꿀 수 있습니다 · You can change this later</div>
                    </div>
                  )}
                </>
              )}

              <button className="lp-btn-login" onClick={handleSubmit}
                disabled={submitting || !email || !username || !password}>
                {submitting ? 'Registering...' : 'Create Account'}
              </button>

              <p className="lp-support" style={{ marginTop: 12 }}>
                Already have an account? <Link to="/login" style={{ color: '#551FFF', fontWeight: 600 }}>Login</Link>
              </p>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
              <h2 style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>
                {inviteData ? 'Welcome!' : 'Registration Complete!'}
              </h2>
              <p style={{ color: '#888', fontSize: 14 }}>
                {inviteData
                  ? 'Your account is ready. You can now log in.'
                  : 'Your account is pending admin approval.'}
              </p>
              <Link to="/login">
                <button className="lp-btn-login" style={{ marginTop: 24 }}>Go to Login</button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmailRegister;
